import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { DashboardLayout, Card } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import Modal from '../components/Modal';
import { adminApi } from '../services/api';
import type { AdminBank, AdminBankInput } from '../types';
import { apiErrorStatus, saveErrorText } from '../utils/apiError';
import { EnglishStatusBadge } from './AdminBankGuide';

type TriState = 'yes' | 'no' | 'unknown';
type FlagKey = 'customerCanRaise' | 'bankCanRaise' | 'accountTypeAffects';
type AmountKey = 'maxPerTransaction' | 'dailyLimit';

const FLAG_KEYS: FlagKey[] = ['customerCanRaise', 'bankCanRaise', 'accountTypeAffects'];
const AMOUNT_KEYS: AmountKey[] = ['maxPerTransaction', 'dailyLimit'];

type BankForm = {
  name: string;
  maxPerTransaction: string;
  dailyLimit: string;
  customerCanRaise: TriState;
  bankCanRaise: TriState;
  accountTypeAffects: TriState;
  amlSecurityCheck: string;
  notes: string;
};

const toTri = (value: boolean | null): TriState => (value === null ? 'unknown' : value ? 'yes' : 'no');
const fromTri = (value: TriState): boolean | null => (value === 'unknown' ? null : value === 'yes');

const emptyForm: BankForm = {
  name: '',
  maxPerTransaction: '',
  dailyLimit: '',
  customerCanRaise: 'unknown',
  bankCanRaise: 'unknown',
  accountTypeAffects: 'unknown',
  amlSecurityCheck: '',
  notes: ''
};

const formFor = (bank: AdminBank): BankForm => ({
  name: bank.name,
  maxPerTransaction: bank.maxPerTransaction === null ? '' : String(bank.maxPerTransaction),
  dailyLimit: bank.dailyLimit === null ? '' : String(bank.dailyLimit),
  customerCanRaise: toTri(bank.customerCanRaise),
  bankCanRaise: toTri(bank.bankCanRaise),
  accountTypeAffects: toTri(bank.accountTypeAffects),
  amlSecurityCheck: bank.amlSecurityCheck,
  notes: bank.notes
});

/** Whole kronor ≥ 0, or null when empty; undefined when invalid. */
const parseAmount = (value: string): number | null | undefined => {
  const trimmed = value.replace(/\s/g, '');
  if (!trimmed) return null;
  return /^\d+$/.test(trimmed) ? Number(trimmed) : undefined;
};

const moveTo = (list: AdminBank[], from: number, to: number) => {
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

/** Konfiguration → Banker: the banks a Beställare can choose (ClickUp 869eu6fpm). */
export function AdminBankSettings() {
  const { t } = useTranslation();
  const [banks, setBanks] = useState<AdminBank[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'danger'; text: string } | null>(null);
  const [editing, setEditing] = useState<AdminBank | 'new' | null>(null);
  const [confirming, setConfirming] = useState<AdminBank | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);

  useEffect(() => {
    adminApi
      .getBanks()
      .then(setBanks)
      .catch(() => setLoadError(true))
      .finally(() => setIsLoading(false));
  }, []);

  const amount = (value: number | null) =>
    value === null
      ? t('adminBanks.values.notSet')
      : t('adminBanks.values.kronor', { amount: new Intl.NumberFormat('sv-SE').format(value) });
  const flag = (value: boolean | null) => t(`adminBanks.values.${toTri(value)}`);

  const replace = (saved: AdminBank) =>
    setBanks(previous => previous.map(bank => (bank.id === saved.id ? saved : bank)));

  const reorder = async (next: AdminBank[]) => {
    const previous = banks;
    setBanks(next);
    setMessage(null);
    try {
      setBanks(await adminApi.reorderBanks(next.map(bank => bank.id)));
    } catch {
      setBanks(previous);
      setMessage({ type: 'danger', text: t('adminBanks.confirm.orderError') });
    }
  };

  const move = (index: number, offset: number) => {
    const target = index + offset;
    if (target < 0 || target >= banks.length) return;
    void reorder(moveTo(banks, index, target));
  };

  const toggleActive = async (bank: AdminBank) => {
    setIsSaving(true);
    try {
      replace(await adminApi.updateBank(bank.id, { isActive: !bank.isActive }));
      setConfirming(null);
      setMessage({ type: 'success', text: t('adminBanks.editor.saved') });
    } catch (error) {
      setMessage({ type: 'danger', text: saveErrorText(t, error, 'adminBanks.editor.saveError') });
      setConfirming(null);
    } finally {
      setIsSaving(false);
    }
  };

  const moveButtons = (bank: AdminBank, index: number) => (
    <div className="admin-banks-move d-flex gap-1">
      <button type="button" className="btn btn-outline-secondary btn-sm" disabled={index === 0}
        aria-label={t('adminBanks.actions.moveUp', { name: bank.name })} onClick={() => move(index, -1)}>
        <i className="feather-arrow-up" aria-hidden="true"></i>
      </button>
      <button type="button" className="btn btn-outline-secondary btn-sm" disabled={index === banks.length - 1}
        aria-label={t('adminBanks.actions.moveDown', { name: bank.name })} onClick={() => move(index, 1)}>
        <i className="feather-arrow-down" aria-hidden="true"></i>
      </button>
    </div>
  );

  // On one line in the table; the cards below md may wrap.
  const actionButtons = (bank: AdminBank, wrap = false) => (
    <div className={`admin-banks-actions d-flex gap-2 ${wrap ? 'flex-wrap' : 'flex-nowrap'}`}>
      <button type="button" className="btn btn-outline-primary btn-sm" onClick={() => setEditing(bank)}
        aria-label={t('adminBanks.actions.editNamed', { name: bank.name })} data-testid={`admin-bank-edit-${bank.name}`}>
        {t('adminBanks.actions.edit')}
      </button>
      <Link to={`/admin/system/banks/${bank.id}/guide`} className="btn btn-outline-primary btn-sm"
        aria-label={t('adminBanks.actions.guideNamed', { name: bank.name })} data-testid={`admin-bank-guide-${bank.name}`}>
        {t('adminBanks.actions.guide')}
      </Link>
      <button type="button" className={`btn btn-sm ${bank.isActive ? 'btn-outline-danger' : 'btn-outline-success'}`}
        onClick={() => setConfirming(bank)} data-testid={`admin-bank-toggle-${bank.name}`}>
        {bank.isActive ? t('adminBanks.actions.deactivate') : t('adminBanks.actions.reactivate')}
      </button>
    </div>
  );

  const statusBadges = (bank: AdminBank) => (
    <>
      <span className={`badge ${bank.isActive ? 'bg-success' : 'bg-secondary'}`}>
        {bank.isActive ? t('adminBanks.status.active') : t('adminBanks.status.inactive')}
      </span>
    </>
  );

  // A Beställare who uses English sees Swedish or an older translation (ClickUp 869eu8fd9).
  const englishBehind = banks.filter(bank => bank.isActive && ['missing', 'stale'].includes(bank.guidanceEnStatus));

  return (
    <DashboardLayout>
      <div className="page-header mb-4">
        <div className="page-header-left d-flex align-items-center">
          <div className="page-header-title">
            <h5 className="fw-bold mb-0">{t('adminBanks.title')}</h5>
          </div>
          <Breadcrumb items={[
            { label: t('nav.home'), path: '/admin/home' },
            { label: t('appNav.configuration') },
            { label: t('adminBanks.title') }
          ]} />
        </div>
        <div className="page-header-right ms-auto d-none d-sm-block">
          <p className="fw-bold text-muted mb-0">{t('adminBanks.description')}</p>
        </div>
      </div>

      {message && (
        <div className={`alert alert-${message.type} alert-dismissible`} role={message.type === 'danger' ? 'alert' : 'status'}>
          {message.text}
          <button type="button" className="btn-close" aria-label={t('common.close')} onClick={() => setMessage(null)}></button>
        </div>
      )}

      {englishBehind.length > 0 && (
        <div className="alert alert-warning" role="status" data-testid="admin-banks-english-warning">
          {t('adminBanks.englishBehind', { names: englishBehind.map(bank => bank.name).join(', ') })}
        </div>
      )}

      <Card className="mb-4">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <p className="text-muted mb-0">{t('adminBanks.orderHelp')}</p>
          <button type="button" className="btn btn-primary customer-banks-action" onClick={() => setEditing('new')} data-testid="admin-bank-add">
            <i className="feather-plus me-1" aria-hidden="true"></i>{t('adminBanks.add')}
          </button>
        </div>

        {isLoading && (
          <div className="d-flex justify-content-center py-4">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{t('common.loading')}</span>
            </div>
          </div>
        )}
        {loadError && <div className="alert alert-danger mb-0" role="alert">{t('adminBanks.loadError')}</div>}
        {!isLoading && !loadError && banks.length === 0 && <p className="text-muted mb-0">{t('adminBanks.empty')}</p>}

        {!isLoading && banks.length > 0 && (
          <>
            <div className="table-responsive d-none d-xl-block">
              <table className="table table-hover align-middle mb-0" data-testid="admin-banks-table">
                <thead>
                  <tr>
                    <th scope="col">{t('adminBanks.columns.order')}</th>
                    <th scope="col">{t('adminBanks.columns.name')}</th>
                    <th scope="col" className="text-end">{t('adminBanks.columns.maxPerTransaction')}</th>
                    <th scope="col" className="text-end">{t('adminBanks.columns.dailyLimit')}</th>
                    <th scope="col" className="text-end">{t('adminBanks.columns.users')}</th>
                    <th scope="col">{t('adminBanks.columns.english')}</th>
                    <th scope="col">{t('adminBanks.columns.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {banks.map((bank, index) => (
                    <tr
                      key={bank.id}
                      draggable
                      onDragStart={() => setDragIndex(index)}
                      onDragOver={event => { event.preventDefault(); setDropIndex(index); }}
                      onDragEnd={() => { setDragIndex(null); setDropIndex(null); }}
                      onDrop={event => {
                        event.preventDefault();
                        if (dragIndex !== null && dragIndex !== index) void reorder(moveTo(banks, dragIndex, index));
                        setDragIndex(null);
                        setDropIndex(null);
                      }}
                      className={[
                        'admin-banks-row',
                        bank.isActive ? '' : 'is-inactive',
                        dragIndex === index ? 'is-dragging' : '',
                        dropIndex === index && dragIndex !== index ? 'is-drop-target' : ''
                      ].filter(Boolean).join(' ')}
                      data-testid={`admin-bank-row-${bank.name}`}
                    >
                      <td>
                        <div className="d-flex align-items-center gap-2">
                          <i className="feather-menu admin-banks-drag" title={t('adminBanks.actions.drag', { name: bank.name })} aria-hidden="true"></i>
                          {moveButtons(bank, index)}
                        </div>
                      </td>
                      <th scope="row">
                        <span className="d-block fw-semibold">{bank.name}</span>
                        {statusBadges(bank)}
                      </th>
                      <td className="text-end text-nowrap">{amount(bank.maxPerTransaction)}</td>
                      <td className="text-end text-nowrap">{amount(bank.dailyLimit)}</td>
                      <td className="text-end">{bank.userCount}</td>
                      <td><EnglishStatusBadge status={bank.guidanceEnStatus} /></td>
                      <td>{actionButtons(bank)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <ul className="list-unstyled d-xl-none mb-0" data-testid="admin-banks-cards">
              {banks.map((bank, index) => (
                <li key={bank.id} className={`admin-banks-card border rounded p-3 mb-2 ${bank.isActive ? '' : 'is-inactive'}`}>
                  <div className="d-flex justify-content-between align-items-start gap-2 mb-2">
                    <div>
                      <h6 className="fw-bold mb-1">{bank.name}</h6>
                      {statusBadges(bank)}
                    </div>
                    {moveButtons(bank, index)}
                  </div>
                  <dl className="admin-banks-facts small mb-3">
                    <dt>{t('adminBanks.columns.maxPerTransaction')}</dt><dd>{amount(bank.maxPerTransaction)}</dd>
                    <dt>{t('adminBanks.columns.dailyLimit')}</dt><dd>{amount(bank.dailyLimit)}</dd>
                    <dt>{t('adminBanks.columns.customerCanRaise')}</dt><dd>{flag(bank.customerCanRaise)}</dd>
                    <dt>{t('adminBanks.columns.bankCanRaise')}</dt><dd>{flag(bank.bankCanRaise)}</dd>
                    <dt>{t('adminBanks.columns.users')}</dt><dd>{t('adminBanks.userCount', { count: bank.userCount })}</dd>
                    <dt>{t('adminBanks.columns.english')}</dt><dd><EnglishStatusBadge status={bank.guidanceEnStatus} /></dd>
                  </dl>
                  {actionButtons(bank, true)}
                </li>
              ))}
            </ul>
          </>
        )}
      </Card>

      {editing && (
        <BankEditor
          bank={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={saved => {
            if (editing === 'new') setBanks(previous => [...previous, saved]);
            else replace(saved);
            setEditing(null);
            setMessage({ type: 'success', text: t('adminBanks.editor.saved') });
          }}
        />
      )}

      {confirming && (
        <Modal
          title={confirming.isActive
            ? t('adminBanks.confirm.deactivateTitle', { name: confirming.name })
            : t('adminBanks.confirm.reactivateTitle', { name: confirming.name })}
          onClose={() => setConfirming(null)}
          closeAriaLabel={t('common.close')}
          footer={
            <div className="modal-action-row">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setConfirming(null)} disabled={isSaving}>
                {t('common.cancel')}
              </button>
              <button type="button" className={`btn ${confirming.isActive ? 'btn-danger' : 'btn-success'}`}
                onClick={() => void toggleActive(confirming)} disabled={isSaving} data-testid="admin-bank-toggle-confirm">
                {isSaving && <span className="spinner-border spinner-border-sm me-2" role="status"></span>}
                {confirming.isActive ? t('adminBanks.actions.deactivate') : t('adminBanks.actions.reactivate')}
              </button>
            </div>
          }
        >
          <p className="mb-0">
            {confirming.isActive
              ? t('adminBanks.confirm.deactivateBody', { count: confirming.userCount })
              : t('adminBanks.confirm.reactivateBody')}
          </p>
        </Modal>
      )}
    </DashboardLayout>
  );
}

function BankEditor({
  bank,
  onClose,
  onSaved
}: {
  bank: AdminBank | null;
  onClose: () => void;
  onSaved: (bank: AdminBank) => void;
}) {
  const { t } = useTranslation();
  const [form, setForm] = useState<BankForm>(bank ? formFor(bank) : emptyForm);
  const [tried, setTried] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const amounts = {
    maxPerTransaction: parseAmount(form.maxPerTransaction),
    dailyLimit: parseAmount(form.dailyLimit)
  };
  const nameMissing = !form.name.trim();
  const amountsValid = AMOUNT_KEYS.every(key => amounts[key] !== undefined);
  // Only a warning: the bank's figures are what they are (ClickUp 869eu6fpm).
  const limitWarning =
    typeof amounts.maxPerTransaction === 'number' &&
    typeof amounts.dailyLimit === 'number' &&
    amounts.maxPerTransaction > amounts.dailyLimit;

  const set = <K extends keyof BankForm>(key: K, value: BankForm[K]) =>
    setForm(previous => ({ ...previous, [key]: value }));

  const save = async () => {
    setTried(true);
    if (nameMissing || !amountsValid) return;
    const data: AdminBankInput & { name: string } = {
      name: form.name.trim(),
      maxPerTransaction: amounts.maxPerTransaction ?? null,
      dailyLimit: amounts.dailyLimit ?? null,
      customerCanRaise: fromTri(form.customerCanRaise),
      bankCanRaise: fromTri(form.bankCanRaise),
      accountTypeAffects: fromTri(form.accountTypeAffects),
      amlSecurityCheck: form.amlSecurityCheck.trim(),
      notes: form.notes.trim()
    };
    setIsSaving(true);
    setError('');
    try {
      onSaved(bank ? await adminApi.updateBank(bank.id, data) : await adminApi.createBank(data));
    } catch (failure) {
      setError(apiErrorStatus(failure) === 409
        ? t('adminBanks.editor.nameTaken')
        : saveErrorText(t, failure, 'adminBanks.editor.saveError', bank ? 'update' : 'create'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      title={bank ? t('adminBanks.editor.editTitle') : t('adminBanks.editor.createTitle')}
      onClose={onClose}
      closeAriaLabel={t('common.close')}
      size="lg"
      fullscreenBelow="sm"
      footer={
        <div className="modal-action-row">
          <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={isSaving}>
            {t('common.cancel')}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void save()} disabled={isSaving} data-testid="admin-bank-save">
            {isSaving && <span className="spinner-border spinner-border-sm me-2" role="status"></span>}
            {t('common.save')}
          </button>
        </div>
      }
    >
      {error && <div className="alert alert-danger" role="alert">{error}</div>}
      <div className="row g-3">
        <div className="col-12">
          <label className="form-label fw-semibold" htmlFor="admin-bank-name">{t('adminBanks.editor.name')} *</label>
          <input id="admin-bank-name" className={`form-control ${tried && nameMissing ? 'is-invalid' : ''}`}
            value={form.name} maxLength={120} onChange={event => set('name', event.target.value)} />
          {tried && nameMissing && <div className="invalid-feedback">{t('adminBanks.editor.nameRequired')}</div>}
        </div>
        {AMOUNT_KEYS.map(key => {
          const invalid = amounts[key] === undefined;
          return (
            <div className="col-sm-6" key={key}>
              <label className="form-label fw-semibold" htmlFor={`admin-bank-${key}`}>{t(`adminBanks.columns.${key}`)}</label>
              <input id={`admin-bank-${key}`} className={`form-control ${invalid ? 'is-invalid' : ''}`} inputMode="numeric"
                value={form[key]} onChange={event => set(key, event.target.value)} aria-describedby={`admin-bank-${key}-help`} />
              {invalid
                ? <div className="invalid-feedback">{t('adminBanks.editor.amountInvalid')}</div>
                : <div id={`admin-bank-${key}-help`} className="form-text">{t('adminBanks.editor.amountHelp')}</div>}
            </div>
          );
        })}
        {limitWarning && (
          <div className="col-12">
            <div className="alert alert-warning mb-0" role="status" data-testid="admin-bank-limit-warning">{t('adminBanks.editor.limitWarning')}</div>
          </div>
        )}
        {FLAG_KEYS.map(key => (
          <div className="col-sm-4" key={key}>
            <label className="form-label fw-semibold" htmlFor={`admin-bank-${key}`}>{t(`adminBanks.columns.${key}`)}</label>
            <select id={`admin-bank-${key}`} className="form-select" value={form[key]} onChange={event => set(key, event.target.value as TriState)}>
              <option value="yes">{t('adminBanks.values.yes')}</option>
              <option value="no">{t('adminBanks.values.no')}</option>
              <option value="unknown">{t('adminBanks.values.unknown')}</option>
            </select>
          </div>
        ))}
        <div className="col-12">
          <label className="form-label fw-semibold" htmlFor="admin-bank-aml">{t('adminBanks.columns.amlSecurityCheck')}</label>
          <textarea id="admin-bank-aml" className="form-control" rows={2} value={form.amlSecurityCheck} onChange={event => set('amlSecurityCheck', event.target.value)} />
        </div>
        <div className="col-12">
          <label className="form-label fw-semibold" htmlFor="admin-bank-notes">{t('adminBanks.columns.notes')}</label>
          <textarea id="admin-bank-notes" className="form-control" rows={3} value={form.notes} onChange={event => set('notes', event.target.value)} />
        </div>
      </div>
    </Modal>
  );
}
